import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import Lightbox from '../components/Lightbox';
import bannerImg from '../images/rooms/Alma-Suite-BnB-Anzio-Camera-1.jpeg';

const roomsCtx = require.context('../images/rooms', false, /\.(png|jpe?g|svg)$/);
const allImages = roomsCtx.keys().map(k => roomsCtx(k));

function CamerePage() {
    const { isEnLang } = useLanguage();
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const { ref: introRef, inView: introInView } = useInView({ triggerOnce: true, threshold: 0.1 });
    const { ref: featRef,  inView: featInView  } = useInView({ triggerOnce: true, threshold: 0.1 });

    useEffect(() => {
        document.title = isEnLang
            ? 'Rooms – B&B Alma Suite Anzio | Modern Rooms with Garden View'
            : 'Camere – B&B Alma Suite Anzio | Camere Moderne con Vista Giardino';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', isEnLang
            ? 'Discover the modern rooms at B&B Alma Suite between Anzio and Nettuno. Garden view, eco-friendly, breakfast included, reserved parking.'
            : 'Scopri le camere moderne del B&B Alma Suite tra Anzio e Nettuno. Vista giardino, eco-sostenibili, colazione inclusa, parcheggio riservato.'
        );
    }, [isEnLang]);

    const T = isEnLang ? {
        label: 'Our Rooms',
        bannerTitle: 'Rooms',
        bannerSub: 'Modern comfort on the Lazio coast',
        introLabel: 'Comfort & Privacy',
        introTitle: 'Spaces Designed for Your Relaxation',
        introDesc: 'All rooms at Alma Suite offer a private garden view, ensuring silence and total privacy. A welcoming and modern environment a few minutes from the beaches of Anzio and Nettuno, with eco-friendly solutions and all the comforts you need.',
        galleryTitle: 'Room Gallery',
        gallerySub: 'Browse our spaces — click any photo to enlarge',
        featTitle: 'What Every Room Includes',
        featSub: 'Each room is furnished and equipped to guarantee maximum comfort',
        features: [
            { icon: '🌿', title: 'Garden View', desc: 'Private garden view — silence and nature guaranteed.' },
            { icon: '❄️', title: 'Air Conditioning', desc: 'Climate control for every season.' },
            { icon: '📶', title: 'Free Wi-Fi', desc: 'Fast Wi-Fi connection throughout the property.' },
            { icon: '🚿', title: 'Private Bathroom', desc: 'En-suite bathroom with shower and toiletries.' },
            { icon: '🍳', title: 'Breakfast Included', desc: 'Fresh breakfast products every morning.' },
            { icon: '🚗', title: 'Reserved Parking', desc: 'Private parking with electric charging point.' },
            { icon: '🌱', title: 'Eco-friendly', desc: 'Sustainable materials and low-impact technologies.' },
            { icon: '🔒', title: 'Full Privacy', desc: 'Independent access and total privacy.' },
        ],
        ctaTitle: 'Book Your Stay',
        ctaSub: 'Contact us via WhatsApp or email for availability and rates',
        ctaBook: 'Book via WhatsApp',
        ctaEmail: 'Send an Email',
    } : {
        label: 'Le Nostre Camere',
        bannerTitle: 'Camere',
        bannerSub: 'Comfort moderno sulla costa laziale',
        introLabel: 'Comfort & Privacy',
        introTitle: 'Spazi Pensati per il Tuo Riposo',
        introDesc: 'Tutte le camere dell\'Alma Suite offrono una vista privata sul giardino, garantendo silenzio e totale riservatezza. Un ambiente accogliente e moderno a pochi minuti dalle spiagge di Anzio e Nettuno, con soluzioni ecosostenibili e tutti i comfort necessari.',
        galleryTitle: 'Galleria Camere',
        gallerySub: 'Esplora i nostri spazi — clicca su una foto per ingrandirla',
        featTitle: 'Cosa Include Ogni Camera',
        featSub: 'Ogni camera è arredata e attrezzata per garantire il massimo comfort',
        features: [
            { icon: '🌿', title: 'Vista Giardino', desc: 'Affaccio sul giardino privato — silenzio e natura garantiti.' },
            { icon: '❄️', title: 'Aria Condizionata', desc: 'Clima perfetto in ogni stagione.' },
            { icon: '📶', title: 'Wi-Fi Gratuito', desc: 'Connessione veloce in tutta la struttura.' },
            { icon: '🚿', title: 'Bagno Privato', desc: 'Bagno en-suite con doccia e set di cortesia.' },
            { icon: '🍳', title: 'Colazione Inclusa', desc: 'Prodotti freschi per la colazione ogni mattina.' },
            { icon: '🚗', title: 'Parcheggio Riservato', desc: 'Posto auto privato con colonnina di ricarica elettrica.' },
            { icon: '🌱', title: 'Ecosostenibile', desc: 'Materiali sostenibili e tecnologie a basso impatto.' },
            { icon: '🔒', title: 'Totale Privacy', desc: 'Accesso indipendente e massima riservatezza.' },
        ],
        ctaTitle: 'Prenota il Tuo Soggiorno',
        ctaSub: 'Contattaci via WhatsApp o email per disponibilità e tariffe',
        ctaBook: 'Prenota su WhatsApp',
        ctaEmail: 'Scrivici via Email',
    };

    return (
        <div className="inner-page">

            <div className="inner-banner" style={{ backgroundImage: `url(${bannerImg})` }}>
                <div className="inner-banner-overlay">
                    <div className="inner-banner-text">
                        <span className="inner-banner-label">{T.label}</span>
                        <h1>{T.bannerTitle}</h1>
                        <p>{T.bannerSub}</p>
                    </div>
                </div>
            </div>

            <section className="inner-intro">
                <div className="l-768">
                    <motion.div
                        ref={introRef}
                        initial={{ opacity: 0, y: 40 }}
                        animate={introInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="section-label">{T.introLabel}</span>
                        <h2>{T.introTitle}</h2>
                        <p>{T.introDesc}</p>
                    </motion.div>
                </div>
            </section>

            <section className="inner-gallery" style={{ background: 'var(--color-bg-alt)' }}>
                <div className="inner-gallery-title l-1260">
                    <h2>{T.galleryTitle}</h2>
                    <p>{T.gallerySub}</p>
                </div>
                <div className="inner-gallery-grid l-1260">
                    {allImages.map((src, i) => (
                        <img
                            key={i}
                            src={src}
                            alt={isEnLang
                                ? `Room B&B Alma Suite Anzio ${i + 1}`
                                : `Camera B&B Alma Suite Anzio ${i + 1}`}
                            onClick={() => setLightboxIndex(i)}
                            loading="lazy"
                        />
                    ))}
                </div>
            </section>

            <section className="inner-features">
                <motion.div
                    ref={featRef}
                    className="inner-features-inner l-1260"
                    initial={{ opacity: 0, y: 40 }}
                    animate={featInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    <h2>{T.featTitle}</h2>
                    <p className="inner-features-sub">{T.featSub}</p>
                    <div className="inner-features-grid">
                        {T.features.map((f, i) => (
                            <div className="inner-feature-item" key={i}>
                                <span className="inner-feature-icon">{f.icon}</span>
                                <h3>{f.title}</h3>
                                <p>{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </section>

            <section className="inner-cta">
                <h2>{T.ctaTitle}</h2>
                <p>{T.ctaSub}</p>
                <div className="suite-cta-buttons">
                    <a
                        href={`https://wa.me/+393928729907?text=${encodeURIComponent("Salve, vorrei informazioni sulle camere al B&B Alma Suite. www.almasuitebb.it")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary"
                    >{T.ctaBook}</a>
                    <a href="mailto:almasuitebb@gmail.com" className="btn-outline-white">{T.ctaEmail}</a>
                </div>
            </section>

            {lightboxIndex !== null && (
                <Lightbox
                    images={allImages}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                />
            )}
        </div>
    );
}

export default CamerePage;
